import React, { useState, useEffect } from "react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { toast, ToastContainer } from "react-toastify";
import MaterialTable from "material-table";
import CircularProgress from "@material-ui/core/CircularProgress";
import { getTrans as t } from "../../Localization/translations";

import { API, STRIPEPUBLICKEY } from "../../backend";
import authHeader from "../../services/auth-header";
import GetCompanyInfoService from "../../services/company/getCompanyInfoService";
import { C, S } from "./styles";
import OrderAgreement from "./OrderAgreement";

// The "Manage License" tab — same underlying data as the existing
// /payment screen (Your Plan, Subscription Details, license usage,
// Cancel Subscription, Payment History), restyled to match the rest of
// the Subscription pages. License changes (increase/reduce) are built
// fresh here rather than reusing /payment's AddExitingPayment flow, so
// that: (1) proration reads the real per-user rate instead of a
// hardcoded price, (2) reducing licenses is possible at all — the old
// flow only ever added, (3) a visitor can request an ACH bill instead of
// paying by card immediately. All three go through real, existing,
// already-safe endpoints: PUT paymentsub/subscription/ (updates
// no_of_users, and — by never touching start_date/end_date — new
// licenses stay co-terminus with the current term automatically),
// POST paymentsub/subscription/payment/ (logs the payment or the pending
// ACH request), and paymentsub/subscription/create-payment-intent/ for
// the card charge itself. Cancel still uses the same endpoint /payment
// uses.

const stripePromise = loadStripe(STRIPEPUBLICKEY);

function LicensePaymentStep({ amount, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError("");
    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });
    if (confirmError) {
      setError(confirmError.message || "Payment failed. Please try again.");
      setSubmitting(false);
      return;
    }
    if (
      paymentIntent &&
      (paymentIntent.status === "succeeded" || paymentIntent.status === "requires_capture")
    ) {
      onSuccess();
    } else {
      setError("Payment was not confirmed. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement options={{ layout: "tabs" }} />
      {error && <p className="text-danger small mt-2 mb-0">{error}</p>}
      <div className="d-flex gap-2 mt-3">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="border-0 btn btn-sm btn-primary btn-gradient-primary"
          disabled={!stripe || submitting}
        >
          {submitting ? "Confirming Payment..." : `Pay $${amount.toFixed(2)}`}
        </button>
      </div>
    </form>
  );
}

// Shown when the company has no Subscription row at all — either it never
// had one, or a previous cancellation deleted it. Lets the visitor start a
// fresh subscription for their EXISTING company (company_id is passed in
// from the already-loaded company profile) via the plain
// POST paymentsub/subscription/ endpoint. Deliberately does NOT go through
// the public sign-up/ or company/create-client/ flows — those both create
// a brand new company, which is exactly what must not happen here.
function ReactivateSubscriptionPanel({ company, onActivated }) {
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [busy, setBusy] = useState(false);
  const [payStep, setPayStep] = useState(null); // { plan, amount, clientSecret } | null
  const [showAgreement, setShowAgreement] = useState(false);

  const notify = (fn, msg) =>
    fn(msg, { position: "top-right", autoClose: 2500, theme: "light" });

  useEffect(() => {
    axios
      .get(API + "paymentsub/subscription/plan/", { headers: authHeader() })
      .then((res) => {
        if (res.data && res.data.success === 1) setPlans(res.data.data || []);
      })
      .catch(() => { })
      .finally(() => setLoadingPlans(false));
  }, []);

  const freePlan = plans.find((p) => p.is_free_plan || Number(p.per_user_amount) === 0);
  const proPlan =
    plans.find((p) => p.plan_name === "Annual") ||
    plans.find((p) => !p.is_free_plan && Number(p.per_user_amount) > 0);

  const createSubscription = async (plan, amount, subscriptionType) => {
    const res = await axios.post(
      API + "paymentsub/subscription/",
      {
        company_id: company.id,
        plan_id: plan.id,
        Status: "A",
        start_date: new Date().toISOString().slice(0, 10),
        no_of_users: 1,
        total_amount: amount,
        subscription_type: subscriptionType,
      },
      { headers: authHeader() }
    );
    if (!res.data || res.data.success !== 1) {
      throw new Error(
        (res.data && (res.data.error?.non_field_errors?.[0] || JSON.stringify(res.data.error))) ||
        "Couldn't start the subscription."
      );
    }
  };

  const handleFree = async () => {
    if (!freePlan) return;
    setBusy(true);
    try {
      await createSubscription(freePlan, 0, "CASH");
      notify(toast.success, "Free plan activated.");
      onActivated();
    } catch (error) {
      notify(toast.error, error.message);
    } finally {
      setBusy(false);
    }
  };

  const handlePro = async () => {
    if (!proPlan) return;
    setBusy(true);
    try {
      const amount = Number(proPlan.per_user_amount) * 12;
      const res = await axios.post(
        API + "paymentsub/subscription/create-payment-intent/",
        { amount: Math.round(amount * 100), plan: proPlan.plan_name },
        { headers: authHeader() }
      );
      if (res.data && res.data.clientSecret) {
        setPayStep({ plan: proPlan, amount, clientSecret: res.data.clientSecret });
      } else {
        notify(toast.error, (res.data && res.data.error) || "Couldn't start the payment.");
      }
    } catch (error) {
      notify(toast.error, "Couldn't start the payment. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const finalizePro = async () => {
    try {
      await createSubscription(payStep.plan, payStep.amount, "STRIPE");
      notify(toast.success, "Subscription activated.");
      onActivated();
    } catch (error) {
      notify(
        toast.error,
        "Payment succeeded, but the subscription couldn't be activated. Please contact support."
      );
    }
  };

  if (loadingPlans) {
    return (
      <div className="text-center py-5">
        <CircularProgress size={28} />
      </div>
    );
  }

  return (
    <div style={{ ...S.panel, maxWidth: "100%" }}>
      <h3 style={S.planTitle}>
        <span style={{ ...S.dot, background: C.warning }} />
        No Active Subscription
      </h3>
      <p style={S.planDesc}>
        {company ? company.company_name : "This account"} doesn't have an active subscription.
        Choose a plan below to reactivate it — this keeps your existing company and data, it does
        not create a new one.
      </p>

      {showAgreement ? (
        <OrderAgreement
          busy={busy}
          onBack={() => setShowAgreement(false)}
          onAccept={() => {
            setShowAgreement(false);
            handlePro();
          }}
        />
      ) : payStep ? (
        <div style={{ marginTop: 10 }}>
          <div style={S.planRow}>
            <span>Plan</span>
            <strong>{payStep.plan.plan_name}</strong>
          </div>
          <div style={{ ...S.planRow, borderBottom: "none" }}>
            <span>Amount due today</span>
            <strong>${payStep.amount.toFixed(2)}</strong>
          </div>
          <div style={{ marginTop: 14 }}>
            <Elements stripe={stripePromise} options={{ clientSecret: payStep.clientSecret }}>
              <LicensePaymentStep
                amount={payStep.amount}
                onCancel={() => setPayStep(null)}
                onSuccess={finalizePro}
              />
            </Elements>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 12, marginTop: 14, flexWrap: "wrap" }}>
          {freePlan && (
            <button
              type="button"
              style={{ ...S.btn, ...S.btnAuto, ...S.btnOutline }}
              disabled={busy}
              onClick={handleFree}
            >
              Activate Free Plan
            </button>
          )}
          {proPlan && (
            <button
              type="button"
              style={{ ...S.btn, ...S.btnAuto, ...S.btnPrimary }}
              disabled={busy}
              onClick={() => setShowAgreement(true)}
            >
              {busy ? "Preparing..." : `Subscribe to ${proPlan.plan_name} — $${Number(proPlan.per_user_amount).toFixed(2)}/user/month`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function License({ onUpgrade }) {
  const [company, setCompany] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reactivatingCancel, setReactivatingCancel] = useState(false);

  // ── change licenses (increase / reduce) ──────────────────────────────
  const [changeStep, setChangeStep] = useState("closed"); // 'closed' | 'form' | 'agreement' | 'payment'
  const [changeDirection, setChangeDirection] = useState("add"); // 'add' | 'reduce'
  const [quantity, setQuantity] = useState(1); // how many licenses to add/remove — never a "new total"
  const [pendingAction, setPendingAction] = useState(null); // 'card' | 'ach' — which action the agreement step is gating
  const [preparingPayment, setPreparingPayment] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [requestingInvoice, setRequestingInvoice] = useState(false);
  const [updatingLicenses, setUpdatingLicenses] = useState(false);

  const notify = (fn, msg) =>
    fn(msg, { position: "top-right", autoClose: 2500, theme: "light" });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [companyRes, subRes, usersRes] = await Promise.all([
        GetCompanyInfoService.getCompanyInfo(),
        axios.get(API + "paymentsub/subscription/", { headers: authHeader() }),
        axios.get(API + "company/users/", { headers: authHeader() }),
      ]);
      setCompany(companyRes.data);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);

      if (subRes.data && subRes.data.success === 1 && subRes.data.data) {
        const sub = subRes.data.data;
        setSubscription(sub);
        const payRes = await axios.get(
          API + `paymentsub/subscription/payment-id/?subscription_id=${sub.id}`,
          { headers: authHeader() }
        );
        setPayments((payRes.data && payRes.data.data) || []);
      } else {
        setSubscription(null);
        setPayments([]);
      }
    } catch (error) {
      // Leave whatever loaded successfully; sections below just won't render.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-5">
        <CircularProgress size={28} />
      </div>
    );
  }

  if (!subscription) {
    return (
      <div>
        <ToastContainer />
        <ReactivateSubscriptionPanel company={company} onActivated={fetchAll} />
      </div>
    );
  }

  const perUserAmount = subscription.plan ? Number(subscription.plan.per_user_amount) || 0 : 0;
  const planName = (subscription.plan && subscription.plan.plan_name) || "—";
  const trialDays = Number(subscription.trial_days) || 0;
  const trialEnd = (() => {
    if (!trialDays || !subscription.start_date) return null;
    const d = new Date(subscription.start_date);
    d.setDate(d.getDate() + trialDays);
    return d;
  })();
  const inTrial = perUserAmount > 0 && trialEnd && new Date() <= trialEnd;
  const statusLabel = perUserAmount === 0 ? "Free" : inTrial ? "Trial Period" : "Active";
  const statusStyle = perUserAmount === 0 ? S.badgeMuted : inTrial ? S.badgeWarning : S.badgeSuccess;
  const cancelScheduled = !!subscription.cancel_date && subscription.Status !== "C";

  const licenseCount = subscription.no_of_users || 0;
  const registeredCount = users.length;
  const availableLicenses = Math.max(licenseCount - registeredCount, 0);

  // quantity can briefly be "" while the field is being edited (so the
  // visitor can clear the box and type a fresh number instead of it
  // always snapping back to a digit and appending to whatever they type).
  const quantityNum = quantity === "" ? 0 : Number(quantity);
  const delta = changeDirection === "add" ? quantityNum : -quantityNum;
  const newTotal = Math.max(licenseCount + delta, 0);

  // Co-terminus: new licenses are added to the CURRENT term (start_date/
  // end_date are never touched by the update below), so the whole
  // subscription keeps renewing on its existing date instead of a fresh
  // 12 months starting today — but each added license is still billed at
  // the full annual rate, same as any other license on the plan.
  const addAmount = delta > 0 ? perUserAmount * 12 * delta : 0;

  const openChangeLicenses = (direction = "add") => {
    setChangeDirection(direction);
    setQuantity(1);
    setChangeStep("form");
  };
  const switchChangeDirection = (direction) => {
    setChangeDirection(direction);
    setQuantity(1);
  };
  const closeChangeLicenses = () => {
    setChangeStep("closed");
    setClientSecret("");
    setPendingAction(null);
  };

  const applyLicenseCount = async (newCount) => {
    const res = await axios.put(
      API + "paymentsub/subscription/",
      { id: subscription.id, no_of_users: newCount },
      { headers: authHeader() }
    );
    if (!res.data || res.data.success !== 1) {
      throw new Error((res.data && res.data.error) || "Couldn't update licenses.");
    }
  };

  const logPayment = async (paymentMethod, paymentGateway, paymentStatus) => {
    await axios.post(
      API + "paymentsub/subscription/payment/",
      {
        sub_id: subscription.id,
        payment_date: new Date().toISOString().slice(0, 10),
        payment_method: paymentMethod,
        payment_gateway: paymentGateway,
        payment_status: paymentStatus,
        no_of_user: delta,
        total_amount: addAmount,
      },
      { headers: authHeader() }
    );
  };

  const handleReduceOrFree = async () => {
    if (delta === 0) return;
    setUpdatingLicenses(true);
    try {
      await applyLicenseCount(newTotal);
      notify(toast.success, "Licenses updated.");
      closeChangeLicenses();
      fetchAll();
    } catch (error) {
      notify(toast.error, error.message || "Couldn't update licenses.");
    } finally {
      setUpdatingLicenses(false);
    }
  };

  const handlePayWithCard = async () => {
    if (delta <= 0) return;
    setPreparingPayment(true);
    try {
      const res = await axios.post(
        API + "paymentsub/subscription/create-payment-intent/",
        { amount: Math.round(addAmount * 100), plan: planName },
        { headers: authHeader() }
      );
      if (res.data && res.data.clientSecret) {
        setClientSecret(res.data.clientSecret);
        setChangeStep("payment");
      } else {
        notify(
          toast.error,
          (res.data && res.data.error) || "Couldn't start the payment. Please try again."
        );
      }
    } catch (error) {
      notify(toast.error, "Couldn't start the payment. Please try again.");
    } finally {
      setPreparingPayment(false);
    }
  };

  const finalizeCardPayment = async () => {
    try {
      await logPayment("STRIPE", "STRIPE", "success");
      await applyLicenseCount(newTotal);
      notify(toast.success, `Added ${quantityNum} license${quantityNum > 1 ? "s" : ""}.`);
      closeChangeLicenses();
      fetchAll();
    } catch (error) {
      notify(
        toast.error,
        "Payment succeeded, but licenses couldn't be updated. Please contact support."
      );
    }
  };

  const handleRequestInvoice = async () => {
    if (delta <= 0) return;
    setRequestingInvoice(true);
    try {
      await logPayment("ACH", "ADMIN", "pending");
      notify(toast.success, "Request recorded — our team will follow up to arrange ACH payment.");
      closeChangeLicenses();
      fetchAll();
    } catch (error) {
      notify(toast.error, "Couldn't record the request. Please try again.");
    } finally {
      setRequestingInvoice(false);
    }
  };

  // Both paid paths (card, ACH) route through the Order Form agreement
  // first — the buttons below only set which action is pending and move
  // to the 'agreement' step; the actual charge/request only fires once
  // the visitor clicks Accept there.
  const openAgreement = (action) => {
    setPendingAction(action);
    setChangeStep("agreement");
  };
  const handleAgreementAccept = () => {
    if (pendingAction === "card") handlePayWithCard();
    else if (pendingAction === "ach") handleRequestInvoice();
    setPendingAction(null);
  };

  // Schedules cancellation at the end of the current term — access
  // continues, nothing is deleted. (This replaces an earlier version that
  // called paymentsub/canceltrial-subscription/, which deletes the
  // Subscription, Customer, and login User rows outright; that endpoint
  // is only meant for abandoning an unpaid trial signup, not for a real
  // "Cancel Subscription" button.)
  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(
        API + "paymentsub/subscription/cancel-at-term/",
        {},
        { headers: authHeader() }
      );
      if (res.data && res.data.success === 1) {
        notify(toast.success, res.data.message || "Subscription will not renew.");
        setShowCancelConfirm(false);
        fetchAll();
      } else {
        notify(toast.error, (res.data && res.data.error) || "Couldn't cancel subscription.");
      }
    } catch (error) {
      notify(toast.error, "Couldn't cancel subscription. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  const handleUndoCancel = async () => {
    setReactivatingCancel(true);
    try {
      const res = await axios.post(
        API + "paymentsub/subscription/reactivate/",
        {},
        { headers: authHeader() }
      );
      if (res.data && res.data.success === 1) {
        notify(toast.success, res.data.message || "Cancellation undone.");
        fetchAll();
      } else {
        notify(toast.error, (res.data && res.data.error) || "Couldn't undo cancellation.");
      }
    } catch (error) {
      notify(toast.error, "Couldn't undo cancellation. Please try again.");
    } finally {
      setReactivatingCancel(false);
    }
  };

  return (
    <div>
      <ToastContainer />

      {/* <div style={{ marginBottom: 22 }}>
        <div style={S.detailRowLabel}>Account Name</div>
        <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>
          {(company && company.company_name) || "—"}
        </div>
      </div> */}

      <div className="pt-4">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h5 className="mb-0">{t("Current Plan")}</h5>
          {cancelScheduled && changeStep === "closed" && (
            <span className="badge bg-warning text-dark">
              Ends {subscription.end_date ? new Date(subscription.end_date).toLocaleDateString() : ""}
            </span>
          )}
        </div>

        {changeStep === "closed" && (
          <>
            <MaterialTable
              style={{
                backgroundColor: "var(--bs-table-bg)",
                borderBottomWidth: "1px",
                boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)",
              }}
              columns={[
                {
                  title: "Plan",
                  field: "planName",
                  headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                  cellStyle: { textAlign: "left", fontSize: 12 },
                },
                {
                  title: "Fee (per user per month)",
                  field: "fee",
                  headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                  cellStyle: { textAlign: "left", fontSize: 12 },
                },
                {
                  title: "Payable",
                  field: "payable",
                  headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                  cellStyle: { textAlign: "left", fontSize: 12 },
                },
              ]}
              data={[
                {
                  planName,
                  fee: perUserAmount > 0 ? `$${perUserAmount.toFixed(2)}` : "Free",
                  payable: perUserAmount > 0 ? "Annual in Advance" : "—",
                },
              ]}
              options={{
                headerStyle: { backgroundColor: "white", color: "#245C7F", fontSize: 11 },
                rowStyle: (rowData, index) => (index % 2 ? { backgroundColor: "#fff" } : {}),
                sorting: true,
                search: false,
                toolbar: false,
                paging: true,
                pageSize: 10,
                emptyRowsWhenPaging: false,
                pageSizeOptions: [10, 20, 25, 50],
                paginationType: "stepped",
                showFirstLastPageButtons: false,
              }}
            />

            <div className="d-flex justify-content-end gap-2 flex-wrap mt-3">
              {subscription.Status !== "C" && perUserAmount > 0 && (
                <button
                  type="button"
                  className="border-0 btn  btn-primary btn-gradient-primary"
                  onClick={() => openChangeLicenses("add")}
                >
                  + Update Subscription
                </button>
              )}
              {subscription.Status !== "C" && perUserAmount === 0 && (
                <span title="Adding licenses is available after you upgrade from the free plan." style={{ cursor: "not-allowed" }}>
                  <button
                    type="button"
                    className="border-0 btn btn-secondary"
                    disabled
                    aria-disabled="true"
                    style={{ pointerEvents: "none" }}
                  >
                    + Update Subscription
                  </button>
                </span>
              )}
              {subscription.Status !== "C" && perUserAmount === 0 && (
                <button
                  type="button"
                  className="border-0 btn  btn-primary btn-gradient-primary"
                  onClick={onUpgrade}
                >
                  + Upgrade
                </button>
              )}
              {subscription.Status !== "C" && !cancelScheduled && (
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => setShowCancelConfirm(true)}
                >
                  Cancel Subscription
                </button>
              )}
              {cancelScheduled && (
                <button
                  type="button"
                  className="btn btn-sm btn-primary btn-gradient-primary"
                  disabled={reactivatingCancel}
                  onClick={handleUndoCancel}
                >
                  {reactivatingCancel ? "Undoing..." : "Undo Cancellation"}
                </button>
              )}
            </div>
            {perUserAmount === 0 && !cancelScheduled && (
              <p className="text-muted small mt-2 mb-0">
                If a free organization is not used for 3 months, it may be removed.
              </p>
            )}
          </>
        )}

        {changeStep !== "closed" && (
          <div className="card" role="document">
            <div className="card-body">

              {changeStep === "form" && (
                <div>
                  <button
                    type="button"
                    className="btn btn-link ps-0 text-decoration-none text-muted mb-3 d-block"
                    onClick={closeChangeLicenses}
                  >
                    <i className="fa-solid fa-arrow-left me-2" />
                    Back
                  </button>

                  <div className="d-flex gap-2 mb-3" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${changeDirection === "add" ? "btn-primary" : "btn-outline-secondary"
                        }`}
                      onClick={() => switchChangeDirection("add")}
                    >
                      Add Licenses
                    </button>

                    <button
                      type="button"
                      className={`btn btn-sm ${changeDirection === "reduce" ? "btn-primary" : "btn-outline-secondary"
                        }`}
                      disabled={licenseCount <= 1}
                      onClick={() => switchChangeDirection("reduce")}
                    >
                      Reduce Licenses
                    </button>
                  </div>

                  <div className="d-flex align-items-center justify-content-between border-bottom py-3">
                    <div>
                      <div className="fw-medium">
                        {changeDirection === "add" ? "Licenses to Add" : "Licenses to Remove"}
                      </div>
                      <div className="small text-muted">
                        Currently {licenseCount} — new total will be {newTotal}
                      </div>
                    </div>
                    <input
                      type="number"
                      min={1}
                      max={changeDirection === "reduce" ? licenseCount - 1 : undefined}
                      className="form-control"
                      style={{ maxWidth: 110 }}
                      value={quantity}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === "") {
                          setQuantity("");
                          return;
                        }
                        const n = parseInt(raw, 10);
                        if (!isNaN(n)) setQuantity(n);
                      }}
                      onBlur={() => {
                        if (quantity === "" || Number(quantity) < 1) {
                          setQuantity(1);
                        } else if (changeDirection === "reduce" && Number(quantity) > licenseCount - 1) {
                          setQuantity(Math.max(licenseCount - 1, 1));
                        }
                      }}
                    />
                  </div>

                  {quantityNum > 0 && (
                    <div className="mt-3">
                      {changeDirection === "add" ? (
                        <>
                          <div className="fw-semibold">
                            Adding {quantityNum} license{quantityNum > 1 ? "s" : ""}
                          </div>
                          <div className="fs-4 fw-bold mt-2">${addAmount.toFixed(2)}</div>
                          <div className="d-flex gap-2 flex-wrap mt-3">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              disabled={requestingInvoice || preparingPayment}
                              onClick={() => openAgreement("ach")}
                            >
                              {requestingInvoice ? "Requesting..." : "Request Bill (ACH)"}
                            </button>
                            <button
                              type="button"
                              className="border-0 btn btn-sm btn-primary btn-gradient-primary"
                              disabled={preparingPayment || requestingInvoice}
                              onClick={() => openAgreement("card")}
                            >
                              {preparingPayment ? "Preparing..." : "Pay with Card"}
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="fw-semibold">
                            Removing {quantityNum} license{quantityNum > 1 ? "s" : ""}
                          </div>
                          <div className="small text-muted mt-1">
                            Takes effect immediately — no refund is issued for the remaining term.
                          </div>
                          <button
                            type="button"
                            className="border-0 btn btn-sm btn-primary btn-gradient-primary mt-3"
                            disabled={updatingLicenses}
                            onClick={handleReduceOrFree}
                          >
                            {updatingLicenses ? "Updating..." : "Update Licenses"}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {changeStep === "agreement" && (
                <OrderAgreement
                  busy={preparingPayment || requestingInvoice}
                  onBack={() => {
                    setPendingAction(null);
                    setChangeStep("form");
                  }}
                  onAccept={handleAgreementAccept}
                />
              )}

              {changeStep === "payment" && clientSecret && (
                <div>
                  <button
                    type="button"
                    className="btn btn-link ps-0 text-decoration-none text-muted mb-3 d-block"
                    onClick={() => setChangeStep("form")}
                  >
                    <i className="fa-solid fa-arrow-left me-2" />
                    Back
                  </button>
                  <div className="d-flex justify-content-between border-bottom py-2 small">
                    <span className="text-muted">Amount due today</span>
                    <strong>${addAmount.toFixed(2)}</strong>
                  </div>
                  <div className="mt-3">
                    <Elements stripe={stripePromise} options={{ clientSecret }}>
                      <LicensePaymentStep
                        amount={addAmount}
                        onCancel={() => setChangeStep("form")}
                        onSuccess={finalizeCardPayment}
                      />
                    </Elements>
                  </div>
                </div>
              )}

            </div>
          </div>
        )}
      </div>

      {changeStep === "closed" && (
        <div className="pt-4">
          <h5>{t("Subscription Summary")}</h5>
          {/* <span style={{ ...S.badge, ...statusStyle }}>{statusLabel.toUpperCase()}</span> */}


          <MaterialTable
            style={{
              backgroundColor: "var(--bs-table-bg)",
              borderBottomWidth: "1px",
              boxShadow:
                "inset 0 0 0 9999px var(--bs-table-accent-bg)",
            }}
            columns={[
              {
                title: "Start Date",
                field: "start_date",
                headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                cellStyle: { textAlign: "left", fontSize: 12 },
                render: (rowData) =>
                  rowData.start_date ? new Date(rowData.start_date).toLocaleDateString() : "N/A",
              },
              {
                title: "End Date",
                field: "end_date",
                headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                cellStyle: { textAlign: "left", fontSize: 12 },
                render: (rowData) =>
                  perUserAmount > 0 && rowData.end_date
                    ? new Date(rowData.end_date).toLocaleDateString()
                    : "--",
              },
              {
                title: "Renewal Date",
                field: "renewal_date",
                headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                cellStyle: { textAlign: "left", fontSize: 12 },
                render: (rowData) =>
                  perUserAmount > 0 && rowData.renewal_date
                    ? new Date(rowData.renewal_date).toLocaleDateString()
                    : "--",
              },
              {
                title: "Licenses",
                field: "licenseCount",
                headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                cellStyle: { textAlign: "left", fontSize: 12 },
              },
              {
                title: "Available",
                field: "availableLicenses",
                headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                cellStyle: { textAlign: "left", fontSize: 12 },
              },
            ]}
            data={[
              {
                start_date: subscription.start_date,
                end_date: subscription.end_date,
                renewal_date: subscription.renewal_date,
                licenseCount,
                availableLicenses,
              },
            ]}
            options={{
              headerStyle: {
                backgroundColor: "white",
                color: "#245C7F",
                fontSize: 11,
              },
              rowStyle: (rowData, index) =>
                index % 2 ? { backgroundColor: "#fff" } : {},
              sorting: true,
              search: false,
              toolbar: false,
              paging: true,
              pageSize: 10,
              emptyRowsWhenPaging: false,
              pageSizeOptions: [10, 20, 25, 50],
              paginationType: "stepped",
              showFirstLastPageButtons: false,
            }}
          />
        </div>
      )}

      {changeStep === "closed" && (
        <div className="pt-4">
          <h5>{t("Subscription History")}</h5>

          <MaterialTable
            style={{
              boxShadow: "none",
              backgroundColor: "var(--bs-table-bg)",
              borderBottomWidth: "1px",
            }}
            columns={[
              {
                title: "Date",
                field: "created_at",
                headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                cellStyle: { textAlign: "left", fontSize: 12 },
                render: (rowData) =>
                  rowData.created_at ? new Date(rowData.created_at).toLocaleDateString() : "--",
              },
              {
                title: "Licenses",
                field: "no_of_user",
                headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                cellStyle: { textAlign: "left", fontSize: 12 },
                render: (rowData) => rowData.no_of_user ?? rowData.no_of_users ?? "--",
              },
              {
                title: "Payment Date",
                field: "payment_date",
                headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                cellStyle: { textAlign: "left", fontSize: 12 },
                render: (rowData) =>
                  rowData.payment_date ? new Date(rowData.payment_date).toLocaleDateString() : "--",
              },
              {
                title: "Payment Method",
                field: "payment_type",
                headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                cellStyle: { textAlign: "left", fontSize: 12 },
                render: (rowData) => rowData.payment_type || rowData.payment_method || "N/A",
              },
              {
                title: "Payment Reference",
                field: "payment_ref",
                headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                cellStyle: { textAlign: "left", fontSize: 12 },
                render: (rowData) => rowData.payment_ref || rowData.transaction_id || "--",
              },
            ]}
            data={payments}
            options={{
              headerStyle: {
                backgroundColor: "white",
                color: "#245C7F",
                fontSize: 11,
              },
              rowStyle: (rowData, index) =>
                index % 2 ? { backgroundColor: "#fff" } : {},
              sorting: true,
              search: false,
              toolbar: false,
              paging: true,
              pageSize: 10,
              emptyRowsWhenPaging: false,
              pageSizeOptions: [10, 20, 25, 50],
              paginationType: "stepped",
              showFirstLastPageButtons: false,
            }}
          />

        </div>
      )}

      {showCancelConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowCancelConfirm(false)}
        >
          <div
            className="shadow-lg"
            style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              width: "100%",
              maxWidth: 460,
              maxHeight: "90vh",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex align-items-center justify-content-between border-bottom px-4 py-3">
              <h6 className="fw-bold mb-0">Cancel Subscription</h6>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => setShowCancelConfirm(false)}
              />
            </div>
            <div className="px-4 py-3">
              <p className="small mb-0">
                {(company && company.company_name) || "This account"} will stay active until{" "}
                <strong>
                  {subscription.end_date ? new Date(subscription.end_date).toLocaleDateString() : "N/A"}
                </strong>
                , then stop renewing — nothing is deleted, and you can undo this anytime before then.
              </p>
            </div>
            <div className="d-flex justify-content-end gap-2 px-4 pb-4">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowCancelConfirm(false)}
              >
                Keep Subscription
              </button>
              <button
                type="button"
                disabled={cancelling}
                className="btn btn-danger"
                onClick={handleCancelSubscription}
              >
                {cancelling ? "Cancelling..." : "Confirm Cancellation"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default License;
